import * as React from "react";
import { useState } from "react";

import { Field, withTypes } from "react-final-form";
import axios from "axios";

import {
  Button,
  CardActions,
  CircularProgress,
  TextField
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";

import { Notification, useTranslate, useNotify } from "react-admin";

import { ENDPOINTS } from "../apiConstants";
import { getConfigGet } from "../getConfig";
import { ShowData } from "./transferRewardAmount";

const useStyles = makeStyles((theme) => ({
  main: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "flex-start",
    backgroundRepeat: "no-repeat",
    backgroundSize: "cover",
  },
  card: {
    minWidth: 300,
    marginTop: "6em",
  },
  avatar: {
    margin: "1em",
    display: "flex",
    justifyContent: "center",
  },
  form: {
    padding: "0 1em 1em 1em",
  },
  input: {
    marginTop: "1em",
  },
  actions: {
    padding: "0 1em 1em 1em",
  },
  tableCell: {
    padding:'0px 10px 5px 10px',
    border:'1px solid gray' 
  },
  tooltip: {
    padding: '0px'
  },
  resultsContainer: {
    marginTop: "2em",
    width: "100%",
    maxWidth: "800px",
  },
  alignment: {
    margin: '0 35%'
  }
}));

const renderInput = ({
  meta: { touched, error } = { touched: false, error: undefined },
  input: { ...inputProps },
  ...props
}: any) => (
  <TextField
    error={!!(touched && error)}
    helperText={touched && error}
    {...inputProps}
    {...props}
    fullWidth
  />
);

interface FormValues {
  EmailOrPpsAccount?: string | number;
}

const { Form } = withTypes<FormValues>();

const AmlChecks = () => {
  const translate = useTranslate();
  const classes = useStyles();
  const notify = useNotify();

  const [checkLoading, setCheckLoading] = useState(false);
  const [downloadLoading, setDownloadLoading] = useState(false);
  const [amlResults, setAmlResults] = useState([]);
  
  const handleCheckAml = (inputData: any) => {
    setAmlResults([]);
    setCheckLoading(true);
    notify("Fetching AML check results...", "info");

    let config;
    const { EmailOrPpsAccount } = inputData;

    if (isNaN(Number(EmailOrPpsAccount))) {
      config = getConfigGet({ email: EmailOrPpsAccount });
    } else {
      config = getConfigGet({ pps_account_number: EmailOrPpsAccount });
    }

    // Add action parameter for check
    const checkConfig = { ...config, params: { ...config.params, action: 'check' } };
    
    axios.get(ENDPOINTS.AML_VERIFICTION_PPS, checkConfig)
      .then((res: any) => {
        // Handle the response from backend - now directly returning amlResults
        if (res.data && res.data[0]) {
          setAmlResults(res.data[0]);
        } else {
          setAmlResults([]);
        }
        setCheckLoading(false);
        notify("AML check completed successfully", "info", "", undefined, 5000);
      })
      .catch((error) => {
        setCheckLoading(false);
        notify(error?.response?.data?.message || 'Failed to get AML check results', "error", "", undefined, 5000);
      });
  };

  const handleDownloadAml = (inputData: any) => {
    setDownloadLoading(true);
    notify("Preparing AML download...", "info");

    let config;
    const { EmailOrPpsAccount } = inputData;

    if (isNaN(Number(EmailOrPpsAccount))) {
      config = getConfigGet({ email: EmailOrPpsAccount });
    } else {
      config = getConfigGet({ pps_account_number: EmailOrPpsAccount });
    }

    // Add action parameter for download
    const downloadConfig = { ...config, params: { ...config.params, action: 'download' } };
    
    axios.get(ENDPOINTS.AML_VERIFICTION_PPS, downloadConfig)
      .then((res: any) => {
        let fileData = 
          "Name: " + res.data[0] + "\n" +
          "Address: " + res.data[1] + "\n" +
          "Aml xml: " + res.data[2];

        const element = document.createElement("a");
        const file = new Blob([fileData], { type: 'text/plain;charset=utf-8' });
        element.href = URL.createObjectURL(file);
        element.download = res.data[0] + "_aml.txt";
        document.body.appendChild(element);
        element.click();
        document.body.removeChild(element);

        setDownloadLoading(false);
        notify('AML verification file downloaded successfully', "info", "", undefined, 5000);
      })
      .catch((error) => {
        setDownloadLoading(false);
        notify(error?.response?.data?.message || 'Failed to download AML verification file', "error", "", undefined, 5000);
      });
  };

  const validate = (values: FormValues) => {
    const errors: FormValues = {};
    if (!values.EmailOrPpsAccount) {
      errors.EmailOrPpsAccount = translate("ra.validation.required");
    }
    return errors;
  };

  return (
    <>
      <Form
        onSubmit={() => {}} // Empty onSubmit since we handle clicks directly
        validate={validate}
        render={({ values }) => (
          <div>
            <div className={classes.main}>
              <div className={classes.form}>
                <div className={classes.input}>
                  <Field
                    autoFocus
                    name="EmailOrPpsAccount"
                    component={renderInput}
                    label="Email or PPS Account Number"
                    disabled={checkLoading || downloadLoading}
                  />
                </div>
              </div>
              <CardActions className={classes.actions} style={{ display: 'flex', gap: '10px' }}>
                <Button
                  variant="contained"
                  color='primary'
                  disabled={checkLoading || downloadLoading || !values.EmailOrPpsAccount}
                  fullWidth
                  onClick={() => handleCheckAml(values)}
                >
                  {checkLoading && <CircularProgress size={25} thickness={2} />}
                  Check AML
                </Button>
                <Button
                  variant="contained"
                  color='secondary'
                  disabled={checkLoading || downloadLoading || !values.EmailOrPpsAccount}
                  fullWidth
                  onClick={() => handleDownloadAml(values)}
                >
                  {downloadLoading && <CircularProgress size={25} thickness={2} />}
                  Download AML
                </Button>
              </CardActions>
              <Notification />
            </div>

            {amlResults.length > 0 && (
              <div className={classes.resultsContainer}>
                <div className={classes.alignment}>
                  <ShowData record={amlResults} tableCenterAlign={false}/>
                </div>
              </div>
            )}
          </div>
        )}
      />
    </>
  );
};


export default AmlChecks;
